import { useCallback } from "react";
import {
  useLoaderData,
  useNavigation,
  useSearchParams,
  type LoaderFunctionArgs,
} from "react-router-dom";
import { EMPTY_ANALYTICS_DASHBOARD } from "@/types/analytics";
import {
  getDateRangeForPreset,
  parseAnalyticsDateRangeFromUrl,
} from "@/lib/date-utils";
import { queryClient } from "@/lib/queryClient";
import { cn } from "@/lib/utils";
import {
  ANALYTICS_STALE_TIME,
  getAnalyticsDashboardQueryKey,
  useAnalyticsDashboard,
  type AnalyticsLoaderData,
} from "@/hooks/useAnalyticsDashboard";
import { analyticsService } from "@/services/AnalyticsService";
import { AnalyticsHeader } from "@/components/analytics/AnalyticsHeader";
import { KpiGrid } from "@/components/analytics/KpiGrid";
import { TrafficChart } from "@/components/analytics/TrafficChart";
import { TopMetricsTable } from "@/components/analytics/TopMetricsTable";
import { RecentClicksTable } from "@/components/analytics/RecentClicksTable";

export async function analyticsLoader({
  request,
}: LoaderFunctionArgs): Promise<AnalyticsLoaderData> {
  const url = new URL(request.url);
  const dateRange = parseAnalyticsDateRangeFromUrl(url);

  await queryClient.prefetchQuery({
    queryKey: getAnalyticsDashboardQueryKey(
      dateRange.startDate,
      dateRange.endDate,
    ),
    queryFn: () =>
      analyticsService.getDashboard(dateRange.startDate, dateRange.endDate),
    staleTime: ANALYTICS_STALE_TIME,
  });

  return dateRange;
}

export default function Analytics() {
  const { startDate, endDate } = useLoaderData() as AnalyticsLoaderData;
  const navigation = useNavigation();
  const [searchParams, setSearchParams] = useSearchParams();
  const currentPreset = searchParams.get("preset") || "this_month";

  const { data, isPending } = useAnalyticsDashboard({ startDate, endDate });

  const {
    kpi,
    chart: chartData,
    top_campaigns,
    top_landings,
    top_sources,
    recent_clicks,
  } = data ?? EMPTY_ANALYTICS_DASHBOARD;

  const isRouteLoading = navigation.state === "loading";
  const isLoadingData = isRouteLoading || isPending;

  const handleFilterChange = useCallback(
    (preset: string) => {
      const { from, to } = getDateRangeForPreset(preset);
      setSearchParams({ preset, from, to });
    },
    [setSearchParams],
  );

  return (
    <div className="flex-col md:flex">
      <div className="flex-1 space-y-4">
        <AnalyticsHeader
          currentPreset={currentPreset}
          onFilterChange={handleFilterChange}
        />

        <div
          className={cn(
            "space-y-4 transition-opacity",
            isLoadingData && "opacity-60 pointer-events-none",
          )}
        >
          <KpiGrid data={kpi} />

          <TrafficChart data={chartData} />

          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            <TopMetricsTable
              title="Top Campaigns"
              data={top_campaigns}
              type="revenue"
            />
            <TopMetricsTable
              title="Top Landings"
              data={top_landings}
              type="conversions"
            />
            <TopMetricsTable
              title="Top Sources"
              data={top_sources}
              type="conversions"
            />
          </div>

          <RecentClicksTable data={recent_clicks} />
        </div>
      </div>
    </div>
  );
}
